import React, { ReactElement, useMemo, useRef } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, LinearProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ApacheEchartsProps, YAxisDataType } from '../../../interfaces'
import { useGetApacheEchartData } from '../../../../../CustomHooks/EChartHook/EChartHook'
import { useAppSelector } from '../../../../../redux/hook'
import { getAttributeForChartDataSet } from '../../../../../utils/EntityAttributesUtils'
import {
    isChartable,
    isSelect,
    isTimeSeries,
} from '../../../../../utils/AttributeUtils'
import { IKeyValuePair } from '../../../../Entity/interfaces'
import {
    getIndexedColor,
    shouldThresholdLineBeDisplayed,
} from '../../../../../utils/MiscellaneousUtils'
import theme from '../../../../../theme/theme'
import { formatDateTimeWithUserPreferences } from '../../../../../utils/DateAndTimeUtils'
const ApacheEcharts = ({
    viewId,
    selectedAttributes,
    startDate,
    endDate,
    orgId,
    wayPointsSegments,
    showWithinGeofence,
    clampToRelationStart,
}: ApacheEchartsProps): ReactElement => {
    const { t } = useTranslation()

    const chartableAttributes = useMemo(() => {
        return selectedAttributes.filter((attr) => isChartable(attr.attribute))
    }, [selectedAttributes])

    const {
        data: apacheEchartsData,
        isFetching: isFetchingChartData,
        isLoading: isLoadingChartData,
    } = useGetApacheEchartData(
        viewId,
        chartableAttributes,
        startDate,
        endDate,
        orgId,
        clampToRelationStart,
    )
    const chartRef = useRef<any>(null)
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const chartableDataSets = useMemo(() => {
        return (
            apacheEchartsData?.data?.datasets.filter((dataset) => {
                const attribute = getAttributeForChartDataSet(
                    chartableAttributes,
                    dataset.attributeId,
                    dataset.entityInstanceId,
                )
                return isChartable(attribute) && dataset.data.length > 0
            }) || []
        )
    }, [apacheEchartsData, chartableAttributes])

    const labels = useMemo(() => {
        const chartLabels = apacheEchartsData?.data?.labels || []

        const startDateTime = new Date(startDate).getTime()
        const endDateTime = new Date(endDate).getTime()
        const isBetweenSelectedDates = (time: string): boolean => {
            const currentTime = new Date(time).getTime()
            return currentTime >= startDateTime && currentTime <= endDateTime
        }
        /**
         * The below slice(2).slice(-1) ensures only the waypoints start and exit
         * dates are included. It excludes boths Shipper start, end dates.
         * so the chart does not stretch a segment when no event data exists.
         * Keep all intermediate start and end times so waypoints without event
         * data still appear on the chart. Since each segment's end time is the
         * next segment's start time, collecting start times after the first 2
         * segments covers the intermediate boundaries without duplicate labels.
         */
        const wayPointSegmentLabels = wayPointsSegments
            .slice(2)
            .slice(-1)
            .map((seg) => seg.startTime)
            .filter((time): time is string => Boolean(time))

        const filteredWayPointSegmentLabels = wayPointSegmentLabels.filter(
            isBetweenSelectedDates,
        )

        const mergedLabels: string[] = []
        let chartLabelIndex = 0
        let wayPointSegmentLabelIndex = 0

        const appendLabel = (label: string) => {
            if (mergedLabels[mergedLabels.length - 1] !== label) {
                mergedLabels.push(label)
            }
        }

        while (
            chartLabelIndex < chartLabels.length ||
            wayPointSegmentLabelIndex < filteredWayPointSegmentLabels.length
        ) {
            /**
             * This logic chosen because the labels & segments are already sorted by date.
             */
            const chartLabel = chartLabels[chartLabelIndex]
            const wayPointSegmentLabel =
                filteredWayPointSegmentLabels[wayPointSegmentLabelIndex]

            if (
                !wayPointSegmentLabel ||
                (chartLabel &&
                    new Date(chartLabel).getTime() <=
                        new Date(wayPointSegmentLabel).getTime())
            ) {
                appendLabel(chartLabel)
                chartLabelIndex += 1
            } else {
                appendLabel(wayPointSegmentLabel)
                wayPointSegmentLabelIndex += 1
            }
        }

        return mergedLabels
    }, [apacheEchartsData?.data?.labels, endDate, startDate, wayPointsSegments])

    const handleChartReady = (echartsInstance: any) => {
        chartRef.current = echartsInstance

        // Initial zoom cursor activation
        setTimeout(() => {
            echartsInstance.dispatchAction({
                type: 'takeGlobalCursor',
                key: 'dataZoomSelect',
                dataZoomSelectActive: true,
            })
        }, 500)

        // Listen for restore and re-enable drag zoom
        echartsInstance.on('restore', () => {
            setTimeout(() => {
                echartsInstance.dispatchAction({
                    type: 'takeGlobalCursor',
                    key: 'dataZoomSelect',
                    dataZoomSelectActive: true,
                })
            }, 100)
        })
    }

    const yAxisLines = useMemo(() => {
        const visitedYAxes: string[] = []
        const lines: YAxisDataType[] = []
        chartableDataSets.forEach((d) => {
            if (visitedYAxes.includes(d.units)) {
                return
            }
            if (d.units) {
                lines.push({
                    key: d.units,
                    isStepper: false,
                    entityInstanceId: d.entityInstanceId,
                })
                visitedYAxes.push(d.units)
            } else {
                lines.push({
                    key: d.attributeId,
                    isStepper: true,
                    entityInstanceId: d.entityInstanceId,
                })
            }
        })
        return lines
    }, [chartableDataSets])

    const nonStepperLines: string[] = []
    const yAxis = yAxisLines.map(({ key, isStepper, entityInstanceId }) => {
        let min,
            max,
            position,
            sameSideCount = 0
        if (!isStepper) {
            const thresholdValues = chartableDataSets
                .filter((d) => d.units === key && d?.rules)
                .flatMap((d) =>
                    Object.values(d.rules).filter((v) => typeof v === 'number'),
                )

            const unitData = chartableDataSets
                .filter((d) => d.units === key)
                .flatMap((d) => d.data.map(([, num]) => num))

            const allValues = [...unitData, ...thresholdValues]

            const numericValues = allValues.filter(
                (v): v is number => typeof v === 'number',
            )

            const dataMin = Math.min(...numericValues)
            const dataMax = Math.max(...numericValues)

            const padding = dataMin === dataMax ? 1 : (dataMax - dataMin) * 0.05

            min = Math.floor(dataMin - padding)
            max = Math.ceil(dataMax + padding)

            if (key === '%') {
                // Percent can’t go below 0 or above 100
                min = Math.max(min, 0)
                max = Math.min(max, 100)
            } else if (key === 'Lux') {
                min = Math.max(min, 0)
            }

            position = nonStepperLines.length % 2 === 0 ? 'left' : 'right'
            sameSideCount = nonStepperLines.length / 2
            nonStepperLines.push(key)
        }

        const offset = sameSideCount * 70 // gap between each axis
        const attribute = getAttributeForChartDataSet(
            chartableAttributes,
            key,
            entityInstanceId,
        )
        const range: IKeyValuePair[] = JSON.parse(
            attribute?.range?.trim() || '[]',
        )
        const rangeKeys = range.map((r) => r.key)

        return {
            type: isStepper ? 'category' : 'value',
            show: !isStepper,
            data: isStepper ? rangeKeys : undefined,
            splitNumber: 3,
            position,
            offset,
            min,
            max,
            axisLabel: {
                formatter: (val: number) => `${val} ${key}`,
                color:
                    chartableDataSets.find((d) => d.units === key)
                        ?.colourCode || '#000',
                show: !isStepper,
                fontSize: '15px',
            },
            axisLine: { show: true },
            axisTick: {
                show: true,
            },
            splitLine: {
                show: false,
                lineStyle: {
                    type: 'dashed',
                    color: '#ccc',
                },
            },
        }
    })

    const showVisualMap = useMemo(
        () =>
            chartableDataSets.some((dataset) => {
                const hasRules = Object.values(dataset.rules || {}).filter(
                    (value) => value !== null && !Number.isNaN(value),
                )

                return hasRules.length > 0
            }),
        [chartableDataSets],
    )

    const wayPointsSegmentsNames = useMemo(() => {
        return wayPointsSegments.map((seg) => seg.segmentName)
    }, [wayPointsSegments])

    const waypointMarkAreaSeriesList = useMemo(() => {
        return wayPointsSegments
            .filter((seg) => seg.startTime && seg.endTime)
            .filter((seg) =>
                showWithinGeofence
                    ? seg.isWithinGeofence
                    : !seg.isWithinGeofence,
            )
            .map((seg, i) => {
                const color = getIndexedColor(i)

                const legendColor = getIndexedColor(i, 1)

                return {
                    name: seg.segmentName,
                    color,
                    legendColor,
                    series: {
                        name: seg.segmentName,
                        type: 'line',
                        data: [[seg.startTime, null]],
                        markArea: {
                            silent: true,
                            data: [
                                [
                                    {
                                        name: seg.segmentName,
                                        xAxis: seg.startTime,
                                        itemStyle: { color },
                                        label: {
                                            show: true,
                                            position: 'top',
                                            offset: [0, -20 * (i % 2)],
                                            backgroundColor: getIndexedColor(
                                                i,
                                                0.8,
                                            ),
                                            color: '#000',
                                            padding: [4, 8],
                                            borderRadius: 4,
                                            fontFamily: 'Roboto, sans-serif',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            textShadowColor:
                                                'rgba(255,255,255,0.9)',
                                            textShadowBlur: 2,
                                            textShadowOffsetX: 1,
                                            textShadowOffsetY: 1,
                                        },
                                    },
                                    {
                                        xAxis: seg.endTime,
                                    },
                                ],
                            ],
                        },
                        lineStyle: { opacity: 0 },
                        showSymbol: false,
                    },
                }
            })
    }, [showWithinGeofence, wayPointsSegments])

    const series = useMemo(() => {
        const chartSeries = chartableDataSets.map((dataset) => {
            const yAxisIndex = yAxisLines.findIndex((y) =>
                y.isStepper
                    ? y.key === dataset.attributeId
                    : y.key === dataset.units,
            )
            const attribute = getAttributeForChartDataSet(
                chartableAttributes,
                dataset.attributeId,
                dataset.entityInstanceId,
            )
            const range: IKeyValuePair[] = JSON.parse(
                attribute?.range.trim() || '[]',
            )
            const isStepLine = isSelect(attribute) && isTimeSeries(attribute)

            return {
                name: `${dataset.label} ${dataset.units}`,
                type: dataset?.data?.length > 1 ? 'line' : 'scatter',
                step: isStepLine ? 'start' : '',
                yAxisIndex,
                data: dataset.data,
                smooth: !isStepLine,
                symbol: 'circle',
                symbolSize: 12,
                showSymbol: isStepLine,
                lineStyle: {
                    width: 2,
                    color: dataset.colourCode,
                },
                itemStyle: {
                    color: dataset.colourCode,
                },
                label: isStepLine
                    ? {
                          show: true,
                          position: 'top',
                          fontSize: 12,
                          color: dataset.colourCode,
                          distance: 8,
                          formatter: (params: any): string => {
                              const value =
                                  range.find(
                                      (r: IKeyValuePair) =>
                                          r.key === params.value[1],
                                  )?.value || ''
                              return value
                          },
                      }
                    : undefined,
                markLine: showVisualMap
                    ? {
                          silent: false,
                          symbol: 'none',
                          data:
                              dataset.rules && typeof dataset.rules === 'object'
                                  ? Object.entries(dataset.rules)
                                        .filter(
                                            ([thresholdType, val]) =>
                                                val !== null &&
                                                typeof val === 'number' &&
                                                shouldThresholdLineBeDisplayed(
                                                    thresholdType,
                                                ),
                                        )
                                        .map(([key, val]) => {
                                            const unit = dataset.units || ''

                                            return {
                                                name: key,
                                                yAxis: val,
                                                lineStyle: {
                                                    type: 'dashed',
                                                    color: dataset.colourCode,
                                                    width: 2,
                                                },
                                                label: {
                                                    formatter: `${Number(
                                                        val,
                                                    ).toFixed(1)}${unit}`,
                                                    position: 'start',
                                                    color: 'white',
                                                    fontSize: 12,
                                                    backgroundColor:
                                                        theme.palette.primary
                                                            .main,
                                                    padding: [2, 2],
                                                },
                                            }
                                        })
                                  : [],
                          label: {
                              show: showVisualMap,
                          },
                      }
                    : undefined,
                markArea: undefined,
            }
        })

        const waypointSeries = waypointMarkAreaSeriesList.map(
            (item) => item.series,
        )

        return [...chartSeries, ...waypointSeries]
    }, [
        chartableDataSets,
        chartableAttributes,
        showVisualMap,
        waypointMarkAreaSeriesList,
        yAxisLines,
    ])

    const option = useMemo(() => {
        return {
            toolbox: {
                show: true,
                top: '-1%',
                right: '1%',
                iconStyle: {
                    borderColor: '#0285ad',
                },
                feature: {
                    restore: {
                        show: true,
                        emphasis: {
                            iconStyle: {
                                textPosition: 'top',
                            },
                        },
                    },
                    saveAsImage: {
                        emphasis: {
                            iconStyle: {
                                textPosition: 'top',
                            },
                        },
                    },

                    dataZoom: {
                        icon: {
                            zoom: 'path://', // hack to remove zoom button
                            back: 'path://', // hack to remove restore button
                        },
                        yAxisIndex: 'none',
                        xAxisIndex: 0,
                    },
                },
                itemSize: 15,
            },
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#fff',
                formatter: (params: any[]): string => {
                    if (!params?.length) return ''

                    const time = formatDateTimeWithUserPreferences(
                        params.find((param) => param.axisType === 'xAxis.time')
                            ?.axisValue,
                        userLocalization,
                    )
                    const tooltipLines = params
                        .filter(
                            (param) =>
                                param.axisType === 'xAxis.time' &&
                                !wayPointsSegmentsNames.includes(
                                    param.seriesName,
                                ),
                        )
                        .map((item) => {
                            const color = item.color
                            const label = item.seriesName.substring(
                                0,
                                item.seriesName.lastIndexOf(' '),
                            )
                            const dataset = chartableDataSets.find(
                                (d) => d.label === label,
                            )
                            const attribute = getAttributeForChartDataSet(
                                chartableAttributes,
                                dataset?.attributeId || '',
                                dataset?.entityInstanceId || '',
                            )
                            const range: IKeyValuePair[] = JSON.parse(
                                attribute?.range.trim() || '[]',
                            )
                            const isStepper = !dataset?.units
                            let formattedValue: string
                            if (Array.isArray(item.data?.value)) {
                                formattedValue = item.data.value[1]
                            } else if (Array.isArray(item.data)) {
                                formattedValue = item.data[1]
                            } else {
                                formattedValue = item.data
                            }

                            const value = isStepper
                                ? range.find(
                                      (r: IKeyValuePair) =>
                                          r.key === formattedValue,
                                  )?.value || formattedValue
                                : `${Number(formattedValue).toFixed(1)} ${dataset?.units}`
                            if (
                                label === '' ||
                                value === null ||
                                value === undefined ||
                                value === 'null'
                            ) {
                                return ''
                            }

                            return `<div style="margin:2px">
                                        <span style="font-family:Roboto; font-weight:700; font-size:14px;color:${color};">${label}:</span>
                                        <span style="font-family:Roboto;font-weight:450;font-size:14px;color: black;">${value}</span>
                                    </div>`
                        })

                    if (!tooltipLines?.length) {
                        return ''
                    }

                    return `${tooltipLines.join('')}
                            <div style="margin:2px;">
                                <span style="font-family:Roboto;font-weight:600;font-size:14px;color: black;">Time:</span>
                                <span style="font-family:Roboto;font-weight:500;font-size:14px;color: black;">${time}</span>
                            </div>`
                },
            },

            dataZoom: [
                {
                    show: true,
                    realtime: true,
                    start: 0,
                    end: 100,
                    bottom: 20,
                    xAxisIndex: [0],
                    labelFormatter: (): string => '',
                },
                {
                    type: 'select',
                    xAxisIndex: 0,
                    filterMode: 'filter',
                    fill: 'black',
                    bottom: 0,
                },
            ],
            grid: [
                {
                    top: '12%',
                    left: '5%',
                    right: '8%',
                    bottom: 120,
                    containLabel: true,
                },
            ],

            xAxis: {
                type: 'time',
                splitNumber: 3,
                axisLine: {
                    show: true,
                    onZero: false,
                },
                data: {
                    value: labels,
                },
                min: labels[0],
                max: labels[labels.length - 1],
                position: 'bottom',
                axisTick: {
                    alignWithLabel: true,
                },
                axisLabel: {
                    formatter: (value: string): string => {
                        const fullDateTime = formatDateTimeWithUserPreferences(
                            value,
                            userLocalization,
                        )
                        const [datePart, timePart, AM_PM] =
                            fullDateTime.split(' ')
                        return AM_PM
                            ? `${datePart}\n${timePart} ${AM_PM}`
                            : `${datePart}\n${timePart}`
                    },
                    padding: 4,
                    fontSize: '12px',
                    showMinLabel: true,
                    showMaxLabel: true,
                    interval: 'auto',
                    hideOverlap: true,
                },
                boundaryGap: false,
            },

            yAxis,
            series,

            legend: {
                show: true,
                bottom: '60',
                left: 'center',
                orient: 'horizontal',
                type: 'scroll',
                pageButtonItemGap: 5,
                pageButtonGap: 30,
                pageIconSize: 15,
                animation: true,
                data: series.map((s) => {
                    const waypointSegment = waypointMarkAreaSeriesList.find(
                        (seg) => seg.name === s.name,
                    )

                    const isWaypoint = Boolean(waypointSegment)
                    const markColor = waypointSegment?.legendColor

                    return {
                        name: s.name,
                        textStyle: {
                            color: isWaypoint
                                ? '#000' // black for segment names because the waypoint colors are lighter.
                                : chartableDataSets.find((d) =>
                                      s.name.startsWith(d.label),
                                  )?.colourCode || '#888', // original color for chart series
                            fontWeight: 'normal',
                            fontSize: 16,
                            fontFamily: theme.typography.body2.font,
                        },
                        itemStyle: {
                            color: isWaypoint
                                ? markColor
                                : chartableDataSets.find((d) =>
                                      s.name.startsWith(d.label),
                                  )?.colourCode || '#888',
                        },
                    }
                }),
            },
        }
    }, [
        yAxis,
        series,
        userLocalization,
        chartableDataSets,
        chartableAttributes,
        waypointMarkAreaSeriesList,
        labels,
        wayPointsSegmentsNames,
    ])

    if (isFetchingChartData || isLoadingChartData) {
        return <LinearProgress />
    }

    if (
        !labels.length ||
        !chartableDataSets.length ||
        chartableDataSets.every((ds) => !ds.data?.length)
    ) {
        return (
            <Box
                sx={{
                    paddingBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography>{t('noDataForSelectedPeriod')}</Typography>
            </Box>
        )
    }

    return (
        <ReactECharts
            option={option}
            notMerge={false}
            lazyUpdate={true}
            onChartReady={handleChartReady}
            style={{
                width: '100%',
                height: 550,
                margin: 0,
            }}
        />
    )
}

const ApacheEchartsMemo = React.memo(ApacheEcharts)
export default ApacheEchartsMemo
